import { Box, Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import SGSModalLight from 'components/SGSModalLight';
import { useCustomStyles } from 'core/mui/customStyles';
import errorIcon from './../../../assets/error_icon.svg';
import classNames from 'classnames';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '@material-ui/lab';
import { Responsable, Utilisateur } from '../model/Utilisateur';
import { utilisateurService } from 'core/services';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';

type Props = {
    isModalErrorOpen: boolean;
    setIsModalErrorOpen: any;
    errorCode: string;
    modalErrorTitle: string;
    responsables: Responsable[];
    utilisateur: Utilisateur;
    users: Utilisateur[];
    setInitialUsers: Function;
};

enum FormState {
    INITIAL = 'INITIAL',
    FORM_SUBMITTED = 'FORM_SUBMITTED',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
}

const ModalErrorDeletingUser: React.FC<Props> = ({ isModalErrorOpen, setIsModalErrorOpen, errorCode, modalErrorTitle, responsables, utilisateur, users, setInitialUsers }) => {
    const { t } = useTranslation();
    const commonStyles = useCustomStyles();
    const filteredResponsables = responsables.filter((r) => r.id !== utilisateur.id);
    const [formState, setFormState] = useState<FormState>(FormState.INITIAL);

    useEffect(() => {
        setFormState(FormState.INITIAL);
    }, [utilisateur]);

    const validationSchema = Yup.object().shape({
        responsable: Yup.object()
            .nullable(true)
            .shape({
                id: Yup.number().required('Responsable ID is required'),
                prenom: Yup.string().required('Responsable first name is required'),
                nom: Yup.string().required('Responsable last name is required'),
            })
            .default(null)
            .nullable()
            .test('responsable', t('utilisateur.validation.responsableId'), (value) => !!value),
    });

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { responsable: null },
    });

    const { handleSubmit, control, getValues, setValue, reset } = methods;

    const handleClose = () => {
        reset();
        setIsModalErrorOpen(false);
    };

    const handleSendForm = () => {
        setFormState(FormState.FORM_SUBMITTED);
        const selectedResponsable = getValues('responsable');
        utilisateurService
            .replaceResponsable({ oldResponsableId: utilisateur.id, newResponsableId: selectedResponsable.id })
            .then(() => {
                // update manager in table list
                const updatedList = cloneDeep(users).map((user) => (user.responsable?.id === utilisateur.id ? { ...user, responsable: { ...selectedResponsable } } : user));
                setInitialUsers(updatedList);
                setFormState(FormState.SUCCESS);
            })
            .catch(() => {
                setFormState(FormState.ERROR);
            });
    };

    const actionToolbar = (errorCode) => {
        return (
            <Box className="text-right mt-4 mb-4" display="flex" justifyContent="flex-end">
                {errorCode === '1' && (
                    <Box className="mr-2">
                        <Button type="submit" variant="contained" color="primary" className={commonStyles.btnBlue} onClick={handleSubmit(handleSendForm)} disabled={formState !== FormState.INITIAL}>
                            Changer le responsable
                        </Button>
                    </Box>
                )}
                <Button variant="contained" color="primary" className={commonStyles.btnRed} onClick={handleClose}>
                    Fermer
                </Button>
            </Box>
        );
    };
    return (
        <>
            <SGSModalLight
                isOpenModal={isModalErrorOpen}
                setIsOpenModal={setIsModalErrorOpen}
                title={'Anomalie détectée : Impossible de supprimer cet utilisateur'}
                titleIcon={errorIcon}
                actionToolbar={() => actionToolbar(errorCode)}
                handleClose={handleClose}
            >
                <Box style={{ minHeight: '200px' }}>
                    {formState === FormState.INITIAL && errorCode === '1' && (
                        <Box>
                            <Box className={commonStyles.txtBlueDarkBold}>{modalErrorTitle} </Box>
                            <Box className={commonStyles.txtBlueLight}>Pour supprimer cet utilisateur, veuillez d'abord désigner un autre responsable.</Box>
                            <Box className={classNames(commonStyles.txtBlueLightBold, 'mt-4')}>Vous pouvez désigner un nouveau responsable pour l'ensemble des utilisateurs concernés :</Box>
                            <Box className="mt-2">
                                <FormProvider {...methods}>
                                    <form id="changeResponsable" onSubmit={handleSubmit(handleSendForm)}>
                                        <Controller
                                            name={'responsable'}
                                            control={control}
                                            render={({ field: { ref }, fieldState: { error } }) => (
                                                <>
                                                    <Autocomplete
                                                        id={'responsable'}
                                                        ref={ref}
                                                        multiple={false}
                                                        noOptionsText="Pas de résultat"
                                                        disableListWrap
                                                        fullWidth
                                                        options={filteredResponsables}
                                                        onChange={(_, data) => {
                                                            setValue('responsable', data);
                                                        }}
                                                        value={filteredResponsables.find((r) => r === getValues('responsable'))}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                error={!!error}
                                                                {...params}
                                                                label={t('utilisateurs.list.responsable' as string)}
                                                                helperText={error?.message}
                                                                variant={'outlined'}
                                                            />
                                                        )}
                                                        getOptionLabel={(option: any) => {
                                                            if (option?.prenom) return option.prenom + ' ' + option.nom;
                                                            return '';
                                                        }}
                                                    />
                                                </>
                                            )}
                                        />
                                    </form>
                                </FormProvider>
                            </Box>
                        </Box>
                    )}
                    {formState === FormState.INITIAL && errorCode !== '1' && <Box className={commonStyles.txtBlueLightBold}>{modalErrorTitle}</Box>}
                    {formState === FormState.FORM_SUBMITTED && (
                        <Box className="pt-5" style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', justifyContent: 'center' }}>
                            <Box style={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={40} style={{ marginRight: '10px' }} />
                                <Typography style={{ marginBottom: '0' }} className={commonStyles.txtBlueLightBold}>
                                    En cours de traitement...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {formState === FormState.SUCCESS && (
                        <Box className="pt-5" style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', justifyContent: 'center' }}>
                            <Box>
                                <Box className={commonStyles.txtBlueDarkBold}>La modification a été enregistrée avec succès.</Box>
                                <Box className={commonStyles.txtBlueDark}>Vous avez maintenant la possibilité de supprimer cet utilisateur.</Box>
                            </Box>
                        </Box>
                    )}
                    {formState === FormState.ERROR && (
                        <Box>
                            <Box className="pt-5">
                                <Box className={classNames(commonStyles.txtRedDarkBold, 'text-center')} display={'flex'} justifyContent={'center'}>
                                    <Box className={commonStyles.icon}>
                                        <i className="fas fa-exclamation-circle"></i>
                                    </Box>
                                    <Box className={classNames(commonStyles.iconText, commonStyles.txtRedDarkBold)}>Une erreur est survenue !</Box>
                                </Box>
                            </Box>
                            <Box className={classNames(commonStyles.txtBlueLight, 'text-center')}>Veuillez réessayer ultérieurement ou prendre contact avec le support</Box>
                        </Box>
                    )}
                </Box>
            </SGSModalLight>
        </>
    );
};

export default ModalErrorDeletingUser;
